import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import EcoIcon from "../../../components/eco-icon/EcoIcon";
import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import EcoSegmented from "../../../components/eco-ui/EcoSegmented";
import EcoPagination from "../../../components/eco-ui/EcoPagination";
import CreateUserModal from "../../../components/modal/create-user/CreateUserModal";
import CreateFreelancerModal from "../../../components/modal/create-freelancer/CreateFreelancerModal";
import type { RootState } from "../../../store/store";
import { getAuthPayload } from "../../../utils/authToken";

import {
  deactivateUserByIdApi,
  reactivateUserByIdApi,
  UserType,
} from "../../auth/authApi";
import {
  FreelancerPublic,
  deactivateFreelancerByIdApi,
  getFreelancersPaginatedApi,
  reactivateFreelancerByIdApi,
} from "../../freelancer/freelancerApi";
import { formatCpf, orDash } from "../../dp/utils/format";

import { AdminUserListItem, listUsersApi } from "../api/adminUsersApi";
import UserTypeBadge, { USER_TYPE_LABELS, userTypeLabel } from "../components/UserTypeBadge";
import { canManageUser } from "../components/userAccess";
import { accessModuleLabel } from "../../../utils/accessModules";
import UserEditModal from "../components/UserEditModal";
import FreelancerEditModal from "../components/FreelancerEditModal";
import ConfirmToggleActiveModal from "../components/ConfirmToggleActiveModal";
import ImportUsersXlsxModal from "../components/ImportUsersXlsxModal";
import { HOMEPAGE } from "../../home/routes";
import { ADMIN_SISTEMA_PAGE } from "../routes";
import { useGoBack } from "../../../hooks/useGoBack";
import "./AdminSistema.css";
import "../../dp/pages/DpEco.css";

const LIMIT = 10;

type Tab = "usuarios" | "freelancers";
type Status = "active" | "deleted" | "all";

/** Alvo de uma confirmação de desativar/reativar, seja usuário ou freelancer. */
type ToggleTarget = {
  kind: "user" | "freelancer";
  id: string;
  name: string;
  action: "deactivate" | "reactivate";
  blockedReason?: string | null;
};

export default function UsersManagementPage() {
  const navigate = useNavigate();
  const goBack = useGoBack(ADMIN_SISTEMA_PAGE());
  const token = useSelector((state: RootState) => state.auth.accessToken);

  // A aba vive na query-string para que recarregar a página (ou compartilhar o
  // endereço) não jogue o operador de volta na primeira aba.
  const [searchParams, setSearchParams] = useSearchParams();
  const tab: Tab = searchParams.get("tab") === "freelancers" ? "freelancers" : "usuarios";

  const setTab = (next: Tab) => {
    setSearchParams(next === "usuarios" ? {} : { tab: next }, { replace: true });
  };

  /** `sub` do token: quem não pode desativar a si mesmo. */
  const myId = useMemo(() => getAuthPayload(token)?.sub ?? null, [token]);

  // ── Aba "Usuários do sistema" ─────────────────────────────────────────────
  const [users, setUsers] = useState<AdminUserListItem[]>([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersPage, setUsersPage] = useState(1);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const [userSearch, setUserSearch] = useState("");
  const [userQ, setUserQ] = useState("");
  const [userStatus, setUserStatus] = useState<Status>("active");
  const [userTypeFilter, setUserTypeFilter] = useState<UserType | "">("");

  const [editingUser, setEditingUser] = useState<AdminUserListItem | null>(null);
  const [isCreateUserOpen, setIsCreateUserOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);

  // ── Aba "Freelancers" ─────────────────────────────────────────────────────
  const [freelancers, setFreelancers] = useState<FreelancerPublic[]>([]);
  const [freelancersTotal, setFreelancersTotal] = useState(0);
  const [freelancersPage, setFreelancersPage] = useState(1);
  const [loadingFreelancers, setLoadingFreelancers] = useState(false);

  const [freelancerSearch, setFreelancerSearch] = useState("");
  const [freelancerNome, setFreelancerNome] = useState("");
  const [freelancerStatus, setFreelancerStatus] = useState<Status>("active");

  const [editingFreelancer, setEditingFreelancer] = useState<FreelancerPublic | null>(null);
  const [isCreateFreelancerOpen, setIsCreateFreelancerOpen] = useState(false);

  // ── Confirmação de desativar/reativar (compartilhada pelas duas abas) ─────
  const [toggleTarget, setToggleTarget] = useState<ToggleTarget | null>(null);
  const [processingToggle, setProcessingToggle] = useState(false);

  const loadUsers = useCallback(async () => {
    if (!token) return;
    setLoadingUsers(true);
    try {
      const data = await listUsersApi(token, {
        page: usersPage,
        limit: LIMIT,
        q: userQ || undefined,
        status: userStatus,
        userType: userTypeFilter || undefined,
      });
      setUsers(data.items);
      setUsersTotal(data.total);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Erro ao listar usuários.");
    } finally {
      setLoadingUsers(false);
    }
  }, [token, usersPage, userQ, userStatus, userTypeFilter]);

  const loadFreelancers = useCallback(async () => {
    if (!token) return;
    setLoadingFreelancers(true);
    try {
      const data = await getFreelancersPaginatedApi(token, {
        page: freelancersPage,
        limit: LIMIT,
        nome: freelancerNome || undefined,
        status: freelancerStatus,
      });
      setFreelancers(data.items);
      setFreelancersTotal(data.total);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Erro ao listar freelancers.");
    } finally {
      setLoadingFreelancers(false);
    }
  }, [token, freelancersPage, freelancerNome, freelancerStatus]);

  // Cada aba só busca quando está visível — trocar de aba não dispara a outra.
  useEffect(() => {
    if (tab === "usuarios") loadUsers();
  }, [tab, loadUsers]);

  useEffect(() => {
    if (tab === "freelancers") loadFreelancers();
  }, [tab, loadFreelancers]);

  const submitUserSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setUsersPage(1);
    setUserQ(userSearch.trim());
  };

  const submitFreelancerSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setFreelancersPage(1);
    setFreelancerNome(freelancerSearch.trim());
  };

  const clearUserFilters = () => {
    setUserSearch("");
    setUserQ("");
    setUserStatus("active");
    setUserTypeFilter("");
    setUsersPage(1);
  };

  const clearFreelancerFilters = () => {
    setFreelancerSearch("");
    setFreelancerNome("");
    setFreelancerStatus("active");
    setFreelancersPage(1);
  };

  const askToggleUser = (user: AdminUserListItem, desativado: boolean) => {
    setToggleTarget({
      kind: "user",
      id: user.id,
      name: user.nomeCompleto,
      action: desativado ? "reactivate" : "deactivate",
      // O backend também recusa (403), mas avisar antes evita que pareça um
      // erro do sistema em vez de uma regra.
      blockedReason:
        !desativado && user.id === myId
          ? "Você não pode desativar o seu próprio usuário. Peça a outro administrador."
          : null,
    });
  };

  const confirmToggle = async () => {
    if (!toggleTarget || !token) return;

    setProcessingToggle(true);
    try {
      if (toggleTarget.kind === "user") {
        if (toggleTarget.action === "deactivate") {
          await deactivateUserByIdApi(toggleTarget.id);
          toast.success("Usuário desativado.");
        } else {
          await reactivateUserByIdApi(toggleTarget.id);
          toast.success("Usuário reativado.");
        }
        await loadUsers();
      } else {
        if (toggleTarget.action === "deactivate") {
          await deactivateFreelancerByIdApi(token, toggleTarget.id);
          toast.success("Freelancer desativado.");
        } else {
          await reactivateFreelancerByIdApi(token, toggleTarget.id);
          toast.success("Freelancer reativado.");
        }
        await loadFreelancers();
      }
      setToggleTarget(null);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Erro ao alterar a situação.");
    } finally {
      setProcessingToggle(false);
    }
  };

  if (!token) return null;

  const usersHasFilters = Boolean(userSearch || userQ || userStatus !== "active" || userTypeFilter);
  const freelancersHasFilters = Boolean(freelancerSearch || freelancerNome || freelancerStatus !== "active");

  const statusPill = (desativado: boolean) => (
    <span className={`ecoPill${desativado ? " ecoPill--off" : ""}`}>{desativado ? "Desativado" : "Ativo"}</span>
  );

  const emptyRow = (text: string) => (
    <tr>
      <td colSpan={7} className="ecoTable__muted admEmptyCell">
        {text}
      </td>
    </tr>
  );

  return (
    <div className="dpePage">
      <main className="dpePage__main">
        <EcoPageHeader
          crumbs={[
            { label: "Início", onClick: () => navigate(HOMEPAGE()) },
            { label: "Administração do Sistema", onClick: () => navigate(ADMIN_SISTEMA_PAGE()) },
            { label: "Gestão de usuários" },
          ]}
          title="Gestão de usuários"
          subtitle="Contas de acesso ao sistema e cadastro administrativo de freelancers."
          onBack={goBack}
          backLabel="Voltar para a Administração"
          actions={
            tab === "usuarios" ? (
              <>
                <button type="button" className="ecoBtn" onClick={() => setIsImportOpen(true)}>
                  <EcoIcon name="download" size={18} />
                  Importar planilha
                </button>
                <button type="button" className="ecoBtn ecoBtn--primary" onClick={() => setIsCreateUserOpen(true)}>
                  <EcoIcon name="plus" size={18} />
                  Novo usuário
                </button>
              </>
            ) : (
              <button type="button" className="ecoBtn ecoBtn--primary" onClick={() => setIsCreateFreelancerOpen(true)}>
                <EcoIcon name="plus" size={18} />
                Novo freelancer
              </button>
            )
          }
        />

        <EcoSegmented
          label="Tipo de cadastro"
          className="admTabsSegmented"
          options={[
            { value: "usuarios", label: "Usuários do sistema" },
            { value: "freelancers", label: "Freelancers" },
          ]}
          value={tab}
          onChange={setTab}
        />

        {tab === "usuarios" ? (
          <section className="ecoCard dpeList" aria-label="Lista de usuários">
            <form className="dpeList__toolbar" onSubmit={submitUserSearch}>
              <div className="dpeList__filters">
                <label htmlFor="adm-busca-usuario" className="ecoVisuallyHidden">Buscar usuário</label>
                <div className="ecoSearch dpeList__search">
                  <EcoIcon name="search" size={18} className="ecoSearch__icon" />
                  <input
                    id="adm-busca-usuario"
                    type="search"
                    className="ecoInput ecoInput--sm"
                    placeholder="Buscar por nome, e-mail ou CPF"
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                  />
                </div>

                <label htmlFor="adm-situacao-usuario" className="ecoVisuallyHidden">Situação</label>
                <select
                  id="adm-situacao-usuario"
                  className="ecoSelect ecoSelect--sm dpeList__select"
                  value={userStatus}
                  onChange={(e) => {
                    setUsersPage(1);
                    setUserStatus(e.target.value as Status);
                  }}
                >
                  <option value="active">Ativos</option>
                  <option value="deleted">Desativados</option>
                  <option value="all">Todos</option>
                </select>

                <label htmlFor="adm-tipo-usuario" className="ecoVisuallyHidden">Tipo</label>
                <select
                  id="adm-tipo-usuario"
                  className="ecoSelect ecoSelect--sm dpeList__select"
                  value={userTypeFilter}
                  onChange={(e) => {
                    setUsersPage(1);
                    setUserTypeFilter(e.target.value as UserType | "");
                  }}
                >
                  <option value="">Todos os tipos</option>
                  {(Object.keys(USER_TYPE_LABELS) as UserType[]).map((t) => (
                    <option key={t} value={t}>
                      {USER_TYPE_LABELS[t]}
                    </option>
                  ))}
                </select>

                <button type="submit" className="ecoBtn ecoBtn--sm">
                  Buscar
                </button>
                {usersHasFilters ? (
                  <button type="button" className="ecoLinkBtn" onClick={clearUserFilters}>
                    Limpar filtros
                  </button>
                ) : null}
              </div>
              <span className="dpeList__count" aria-live="polite">
                {loadingUsers ? "Carregando..." : `${usersTotal} ${usersTotal === 1 ? "usuário" : "usuários"}`}
              </span>
            </form>

            <div className="ecoTableWrap dpeList__table admList__table">
              <table className="ecoTable">
                <thead>
                  <tr>
                    <th scope="col">Nome</th>
                    <th scope="col">CPF</th>
                    <th scope="col">E-mail</th>
                    <th scope="col">Tipo</th>
                    <th scope="col" className="ecoTable__num">Lojas</th>
                    <th scope="col">Situação</th>
                    <th scope="col" className="ecoTable__num">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingUsers
                    ? emptyRow("Carregando...")
                    : users.length === 0
                      ? emptyRow("Nenhum usuário encontrado.")
                      : users.map((user) => {
                          const desativado = Boolean(user.deletedAt);
                          // Administrador restrito não gerencia administradores nem quem
                          // alcança módulo que ele não tem (ADR 0027).
                          const manageable = canManageUser(token, user);
                          const lockedTitle = manageable ? undefined : "Fora do seu acesso: só o Administrador gerencia este usuário.";
                          return (
                            <tr key={user.id}>
                              <td>
                                <strong>{user.nomeCompleto}</strong>
                              </td>
                              <td className="ecoTable__muted">{formatCpf(user.cpf)}</td>
                              <td className="ecoTable__muted">{orDash(user.email)}</td>
                              <td>
                                <span className="admBadgeRow">
                                  <UserTypeBadge userType={user.userType} />
                                  {(user.extraRoles ?? []).map((role) => (
                                    <span
                                      key={role}
                                      className="admBadge admBadge--gray admBadge--extra"
                                      title="Acesso adicional"
                                    >
                                      + {userTypeLabel(role)}
                                    </span>
                                  ))}
                                  {(user.revokedModules ?? []).map((module) => (
                                    <span
                                      key={module}
                                      className="admBadge admBadge--gray admBadge--extra"
                                      title="Direito retirado"
                                    >
                                      − {accessModuleLabel(module)}
                                    </span>
                                  ))}
                                </span>
                              </td>
                              <td className="ecoTable__num">{user.storeIds?.length ?? 0}</td>
                              <td>{statusPill(desativado)}</td>
                              <td className="ecoTable__num">
                                <div className="admRowActions">
                                  <button
                                    type="button"
                                    className="ecoBtn ecoBtn--sm"
                                    onClick={() => setEditingUser(user)}
                                    disabled={!manageable}
                                    title={lockedTitle}
                                  >
                                    Editar
                                  </button>
                                  <button
                                    type="button"
                                    className={`ecoBtn ecoBtn--sm${desativado ? "" : " ecoBtn--danger"}`}
                                    onClick={() => askToggleUser(user, desativado)}
                                    disabled={!manageable}
                                    title={lockedTitle}
                                  >
                                    {desativado ? "Reativar" : "Desativar"}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                </tbody>
              </table>
            </div>

            <div className="dpeList__pager">
              <EcoPagination
                page={usersPage}
                pageSize={LIMIT}
                total={usersTotal}
                onChange={setUsersPage}
                disabled={loadingUsers}
              />
            </div>
          </section>
        ) : (
          <section className="ecoCard dpeList" aria-label="Lista de freelancers">
            <form className="dpeList__toolbar" onSubmit={submitFreelancerSearch}>
              <div className="dpeList__filters">
                <label htmlFor="adm-busca-freelancer" className="ecoVisuallyHidden">Buscar freelancer</label>
                <div className="ecoSearch dpeList__search">
                  <EcoIcon name="search" size={18} className="ecoSearch__icon" />
                  <input
                    id="adm-busca-freelancer"
                    type="search"
                    className="ecoInput ecoInput--sm"
                    placeholder="Buscar por nome"
                    value={freelancerSearch}
                    onChange={(e) => setFreelancerSearch(e.target.value)}
                  />
                </div>

                <label htmlFor="adm-situacao-freelancer" className="ecoVisuallyHidden">Situação</label>
                <select
                  id="adm-situacao-freelancer"
                  className="ecoSelect ecoSelect--sm dpeList__select"
                  value={freelancerStatus}
                  onChange={(e) => {
                    setFreelancersPage(1);
                    setFreelancerStatus(e.target.value as Status);
                  }}
                >
                  <option value="active">Ativos</option>
                  <option value="deleted">Desativados</option>
                  <option value="all">Todos</option>
                </select>

                <button type="submit" className="ecoBtn ecoBtn--sm">
                  Buscar
                </button>
                {freelancersHasFilters ? (
                  <button type="button" className="ecoLinkBtn" onClick={clearFreelancerFilters}>
                    Limpar filtros
                  </button>
                ) : null}
              </div>
              <span className="dpeList__count" aria-live="polite">
                {loadingFreelancers
                  ? "Carregando..."
                  : `${freelancersTotal} ${freelancersTotal === 1 ? "freelancer" : "freelancers"}`}
              </span>
            </form>

            <div className="ecoTableWrap dpeList__table admList__table">
              <table className="ecoTable">
                <thead>
                  <tr>
                    <th scope="col">Nome</th>
                    <th scope="col">CPF</th>
                    <th scope="col">Contato</th>
                    <th scope="col">UF</th>
                    <th scope="col">Afiliação</th>
                    <th scope="col">Situação</th>
                    <th scope="col" className="ecoTable__num">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingFreelancers
                    ? emptyRow("Carregando...")
                    : freelancers.length === 0
                      ? emptyRow("Nenhum freelancer encontrado.")
                      : freelancers.map((freelancer) => {
                          const desativado = Boolean(freelancer.deletedAt);
                          return (
                            <tr key={freelancer.id}>
                              <td>
                                <strong>{freelancer.nomeCompleto}</strong>
                              </td>
                              <td className="ecoTable__muted">{formatCpf(freelancer.cpf)}</td>
                              <td className="ecoTable__muted">{orDash(freelancer.contato)}</td>
                              <td>{orDash(freelancer.estado)}</td>
                              <td>
                                {freelancer.afiliacao ? (
                                  <span className="admBadge admBadge--blue">{freelancer.afiliacao}</span>
                                ) : (
                                  <span className="ecoTable__faint">—</span>
                                )}
                              </td>
                              <td>{statusPill(desativado)}</td>
                              <td className="ecoTable__num">
                                <div className="admRowActions">
                                  <button
                                    type="button"
                                    className="ecoBtn ecoBtn--sm"
                                    onClick={() => setEditingFreelancer(freelancer)}
                                  >
                                    Editar
                                  </button>
                                  <button
                                    type="button"
                                    className={`ecoBtn ecoBtn--sm${desativado ? "" : " ecoBtn--danger"}`}
                                    onClick={() =>
                                      setToggleTarget({
                                        kind: "freelancer",
                                        id: freelancer.id,
                                        name: freelancer.nomeCompleto,
                                        action: desativado ? "reactivate" : "deactivate",
                                      })
                                    }
                                  >
                                    {desativado ? "Reativar" : "Desativar"}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                </tbody>
              </table>
            </div>

            <div className="dpeList__pager">
              <EcoPagination
                page={freelancersPage}
                pageSize={LIMIT}
                total={freelancersTotal}
                onChange={setFreelancersPage}
                disabled={loadingFreelancers}
              />
            </div>
          </section>
        )}
      </main>

      {/* Modais de criação reaproveitados como estão — só mudaram de casa. */}
      <CreateUserModal
        isOpen={isCreateUserOpen}
        onClose={() => setIsCreateUserOpen(false)}
        onCreated={() => {
          setUsersPage(1);
          loadUsers();
        }}
      />

      <CreateFreelancerModal
        isOpen={isCreateFreelancerOpen}
        token={token}
        onClose={() => setIsCreateFreelancerOpen(false)}
        onCreated={() => {
          setFreelancersPage(1);
          loadFreelancers();
        }}
      />

      <ImportUsersXlsxModal
        isOpen={isImportOpen}
        onClose={() => setIsImportOpen(false)}
        onImported={() => {
          setUsersPage(1);
          loadUsers();
        }}
      />

      <UserEditModal
        isOpen={Boolean(editingUser)}
        user={editingUser}
        onClose={() => setEditingUser(null)}
        onUpdated={loadUsers}
      />

      <FreelancerEditModal
        isOpen={Boolean(editingFreelancer)}
        freelancer={editingFreelancer}
        onClose={() => setEditingFreelancer(null)}
        onUpdated={loadFreelancers}
      />

      <ConfirmToggleActiveModal
        isOpen={Boolean(toggleTarget)}
        action={toggleTarget?.action ?? "deactivate"}
        entityLabel={toggleTarget?.kind === "freelancer" ? "freelancer" : "usuário"}
        name={toggleTarget?.name ?? ""}
        blockedReason={toggleTarget?.blockedReason}
        processing={processingToggle}
        onConfirm={confirmToggle}
        onClose={() => {
          if (!processingToggle) setToggleTarget(null);
        }}
      />
    </div>
  );
}
